import { Trans, useTranslation } from 'react-i18next';
import { useSelectedOrganization } from 'app/state/contexts/UserContext';
import { Link } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';
import Tippy from '@tippyjs/react';
import { useChangeImageName, useDeleteImage, useBatchImages } from 'entities/document';
import { ApiError, api } from 'shared/api/client';
import { useCss } from 'entities/style';
import { ConfirmationModal, DataImage, FileZone, Modal, Spinner, TippyMenu, ToolMenu } from 'app/components';
import { formatDate } from 'shared/lib/date';
import { i18n } from 'shared/i18n';
import { filesize } from 'filesize';
import { ChangeEvent, FormEvent, ReactElement, useRef, useState } from 'react';

import { useCreateAccessLog } from 'entities/access-log';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from 'app/api/permission-queries';
import { useGetDocumentBySuperId, documentKeys, EditorDocument } from 'entities/document';
import { isValidImageName, joinFilename, splitFilename } from './filename';

// Narrowing target for the loose `file` shape that `useBatchImages` returns
// (ImageFileShape there has every field optional). The batch entries are bridged
// to PictureData via the single boundary cast in ImageList.
interface ImageFile {
  revisionId: string;
  superId: string;
  documentSuperId: number | string;
  filename: string;
  fileType: string;
  fileContent: string;
  createdAt?: string;
}

interface ImageOccurence {
  sectionSuperId: string;
  pageId?: string;
  pageIndex: number;
}

interface ImageMetadata {
  size?: number;
  width?: number;
  height?: number;
}

interface PictureData {
  file: ImageFile;
  metadata: ImageMetadata;
  occurences: ImageOccurence[];
}

// currentImage holds either a full PictureData (details/edit flows) or just a
// filename (delete flow), so every field is optional.
interface CurrentImage {
  file?: ImageFile;
  metadata?: ImageMetadata;
  occurences?: ImageOccurence[];
  filename?: string;
}

interface UploadResult {
  data: { revisionId: string; superId: string };
}

interface OldImageRef {
  revisionId?: string;
}

type UploadSuccessHandler = (result: UploadResult, old?: OldImageRef) => void;

type ImageMenuItemsFactory = (data: PictureData) => MenuItem[];

interface MenuItem {
  text: string;
  icon: string;
  onClick?: () => void;
  sub?: (item: ReactElement) => ReactElement;
}

type CreateAccessLog = ReturnType<typeof useCreateAccessLog>['mutate'];

interface PictureProps {
  listType: string;
  imageData?: PictureData;
  imageMenuItems: ImageMenuItemsFactory;
  documentSuperId?: string;
}

const Picture = ({ listType, imageData, imageMenuItems, documentSuperId }: PictureProps) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const pictureRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const { mutate: createAccessLog } = useCreateAccessLog();
  const organizationId = useSelectedOrganization();
  const { sectionPermissions } = usePermissions(documentSuperId as number | string);

  if (!imageData) {
    return <Spinner />;
  }

  const data = imageData;
  function renderBoxPicture() {
    return (
      <div className={`picture ${tooltipVisible ? 'active' : ''}`} ref={pictureRef}>
        <div className="img-wrapper">
          <DataImage {...data.file} />
        </div>
        <div className="file-name">
          <span className="file-name-text">{data.file.filename}</span>
          <Tippy
            interactive
            placement="right-end"
            getReferenceClientRect={() => pictureRef.current?.getBoundingClientRect() ?? new DOMRect()}
            appendTo={() => document.body}
            zIndex={9}
            duration={0}
            popperOptions={{
              modifiers: [
                { name: 'flip', enabled: false },
                { name: 'preventOverflow', options: { mainAxis: false, altAxis: true } },
              ],
            }}
            trigger="click"
            content={<ToolMenu menuItems={imageMenuItems(data)} />}
            onShow={() => setTooltipVisible(true)}
            onHide={() => setTooltipVisible(false)}
          >
            <button className="picture-menu-button" aria-label={i18n('pictures:list-selector.list.thead.actions')}>
              <i className="fa-regular fa-ellipsis-vertical" />
            </button>
          </Tippy>
        </div>
      </div>
    );
  }

  function renderListPicture() {
    return (
      <tr ref={rowRef}>
        <td className="image-cell">
          <DataImage {...data.file} />
        </td>
        <td className="text-center page-cell">
          {formatImageOccurences(data.occurences, documentSuperId, sectionPermissions, createAccessLog, organizationId)}
        </td>
        <td className="name-cell">
          <span>{data.file.filename}</span>
        </td>
        <td className={data.file.fileContent?.length > 1000000 ? 'text-danger' : ''}>{sizeWithVolume(data.metadata.size ?? 0)}</td>
        <td>{formatDate(data.file.createdAt)}</td>
        <td>
          <TippyMenu
            menu={imageMenuItems(data)}
            placement="right-end"
            getReferenceClientRect={() => rowRef.current?.getBoundingClientRect() ?? new DOMRect()}
            popperOptions={{
              modifiers: [
                { name: 'flip', enabled: false },
                { name: 'preventOverflow', options: { mainAxis: false, altAxis: true } },
              ],
            }}
          />
        </td>
      </tr>
    );
  }

  switch (listType) {
    case 'box':
      return renderBoxPicture();
    case 'list':
    default:
      return renderListPicture();
  }
};

function sizeWithVolume(size: number) {
  return filesize(size, { base: 10, round: 1 });
}

const formatImageOccurences = (
  occurences: ImageOccurence[] | undefined,
  documentId: string | undefined,
  sectionPermissions: Record<string, string>,
  createAccessLog: CreateAccessLog,
  organizationId: number
) => {
  if (!occurences || occurences.length === 0) {
    return <span>-</span>;
  }
  return occurences.map((occurence, index) => {
    const hasPermission = sectionPermissions[occurence.sectionSuperId];
    return (
      <div key={index} className="occurences">
        <span>
          {hasPermission ? (
            <Link
              to="/documents/$documentId/sections/$sectionId"
              params={{ documentId: String(documentId), sectionId: String(occurence.sectionSuperId) }}
              hash={occurence?.pageId}
              className="occurence-item"
              onClick={() => createAccessLog({ sectionSuperId: occurence.sectionSuperId, documentSuperId: documentId as string, organizationId })}
            >
              {i18n('pictures:list-selector.list.thead.page') + ' ' + occurence.pageIndex}
            </Link>
          ) : (
            <Tippy className="gaby-tooltip" content={<p>{i18n('pictures:list-selector.list.thead.no-access')}</p>}>
              <p className="disabled-occurence-item">{i18n('pictures:list-selector.list.thead.page') + ' ' + occurence.pageIndex}</p>
            </Tippy>
          )}
        </span>
      </div>
    );
  });
};

interface UploadFileProps {
  old?: { superId?: string; revisionId?: string };
  onSuccess: UploadSuccessHandler;
  documentId?: string;
}

const UploadFile = ({ old, onSuccess, documentId }: UploadFileProps) => {
  return (
    <div className="add-new-popup filezone-popup">
      <FileZone type="img" old={old} documentId={documentId} onSuccess={onSuccess} />
    </div>
  );
};

const PicturesEmptyState = () => {
  const { t } = useTranslation('pictures');

  return (
    <div className="pictures--empty">
      <h3 className="pictures--empty--title">{t('empty.title')}</h3>
      <p className="pictures--empty--description">
        <Trans
          t={t}
          i18nKey="empty.description"
          components={{
            fab: (
              <span className="pictures--empty--fab" role="img" aria-label="+">
                <i className="fa-regular fa-plus font-15" />
              </span>
            ),
          }}
        />
      </p>
    </div>
  );
};

const Pictures = () => {
  const { t } = useTranslation(['common', 'pictures', 'project-list']);

  const LIST_TYPES = ['list', 'box'];
  const { documentId } = useDocumentParams();
  const queryClient = useQueryClient();
  const { data: doc } = useGetDocumentBySuperId(documentId) as { data?: EditorDocument };
  const css = useCss({ documentSuperId: documentId });
  const [listType, setListType] = useState(LIST_TYPES[1]);
  const [currentImage, setCurrentImage] = useState<CurrentImage>({});
  const [showImgInfo, setShowImgInfo] = useState(false);
  const [modalNewNameOpen, setModalNewNameOpen] = useState(false);
  const [errorImageName, setErrorImageName] = useState(false);
  const [imageName, setImageName] = useState('');
  const [imageExtension, setImageExtension] = useState('');
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [imageInUseError, setImageInUseError] = useState(false);
  const { mutate: changeImageName } = useChangeImageName();
  const { mutate: deleteImage, isPending: isDeletingImage } = useDeleteImage();

  const imageRevisionIds = (doc?.content?.images ?? []).map((img) => img.revisionId);
  const hasImages = imageRevisionIds.length > 0;
  const { data: imageDataMap = {} } = useBatchImages(documentId, imageRevisionIds);

  const fullFileName = joinFilename(imageName, imageExtension);

  const imageMenuItems = (data: PictureData): MenuItem[] => [
    {
      text: t('pictures:dropdown.item.upload-new'),
      icon: 'fa-pen',
      sub: (item) => {
        return (
          <Tippy
            interactive={true}
            offset={[-130, -310]}
            trigger="click"
            content={<UploadFile old={data.file} onSuccess={handleUpdateSuccess} documentId={documentId} />}
          >
            {item}
          </Tippy>
        );
      },
    },
    {
      text: t('pictures:dropdown.item.edit-name'),
      icon: 'fa-pen',
      onClick: () => {
        const { name: fileName, extension } = splitFilename(data.file.filename, data.file.fileType);

        setCurrentImage(data);
        setShowImgInfo(false);
        setImageName(fileName);
        setImageExtension(extension);
        setModalNewNameOpen(true);
      },
    },
    {
      text: t('pictures:dropdown.item.show-details'),
      icon: 'fa-info-circle',
      onClick: () => {
        setCurrentImage(data);
        setShowImgInfo(true);
      },
    },
    {
      text: t('pictures:dropdown.item.delete-picture'),
      icon: 'fa-trash',
      onClick: async () => {
        setImageToDelete(data.file.revisionId);
        setCurrentImage({ filename: data.file.filename });
        setImageInUseError(false);
      },
    },
  ];

  const handleConfirmDelete = async () => {
    deleteImage(
      { documentSuperId: documentId, imageRevisionId: imageToDelete as string },
      {
        onSuccess: () => {
          setShowImgInfo(false);
          setImageToDelete(null);
          reloadData();
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'success',
            text: t('pictures:delete-success'),
            removeTime: 4000,
          });
        },
        onError: (error: unknown) => {
          if (error instanceof ApiError && error.code === 'IMAGE_IN_USE') {
            setImageInUseError(true);
          } else {
            dispatchMessage(EventType.ADD_MESSAGE, {
              type: 'error',
              text: t('pictures:delete-error'),
              removeTime: 4000,
            });
            ServerErrorHandler(error);
          }
        },
      }
    );
  };

  const DetailView = () => {
    const { t } = useTranslation('pictures');

    const { file, metadata, occurences } = currentImage;
    const { mutate: createAccessLog } = useCreateAccessLog();
    const organizationId = useSelectedOrganization();
    const { documentPermissions, sectionPermissions } = usePermissions(documentId);

    return showImgInfo ? (
      <>
        <div className="detail-view pictures">
          <div className="close-info-btn" onClick={() => setShowImgInfo(false)}>
            <i className="fa-regular fa-xmark" />
          </div>
          <p className="title">{file?.filename}</p>
          <div className="img-wrapper">{file && <DataImage {...file} />}</div>
          <div className="info mt-4">
            <div className="columns two mb-3">
              <p className="title">{t('details-view.type.title')}</p>
              <p>{file?.fileType}</p>
            </div>
            <div className="columns two mb-3">
              <p className="title">{t('details-view.type.occurrence')}</p>
              <div className="flex flex-col">{formatImageOccurences(occurences, documentId, sectionPermissions, createAccessLog, organizationId)}</div>
            </div>
            <div className="columns two mb-3">
              <p className="title">{t('details-view.type.filesize')}</p>
              {sizeWithVolume(metadata?.size ?? 0)}
            </div>
            <div className="columns two mb-3">
              <p className="title">{t('details-view.title.uploaded-at')}</p>
              <p>{formatDate(file?.createdAt)}</p>
            </div>
            {documentPermissions.includes('CODER') && (
              <div className="columns two mb-3">
                <p className="title">{t('details-view.title.super_id')}</p>
                <p>{file?.superId}</p>
              </div>
            )}
          </div>
        </div>
      </>
    ) : null;
  };

  const reloadData = () => {
    queryClient.invalidateQueries({
      queryKey: documentKeys.bySuperId(documentId),
    });
  };

  const handleUpdateSuccess = async ({ data }: UploadResult, old?: OldImageRef) => {
    await api.POST('/documents/{superId}/actions', {
      params: { path: { superId: Number(documentId) } },
      body: {
        action: 'update_image',
        revisionId: old?.revisionId,
        data: {
          revisionId: data.revisionId,
          superId: data.superId,
        },
      },
    });

    reloadData();
  };

  const handleCreateSuccess = async ({ data }: UploadResult) => {
    await api.POST('/documents/{superId}/actions', {
      params: { path: { superId: Number(documentId) } },
      body: {
        action: 'create_image',
        data: {
          revisionId: data.revisionId,
          superId: data.superId,
        },
      },
    });

    await reloadData();
  };

  const toggleListType = () => {
    setListType(listType === LIST_TYPES[0] ? LIST_TYPES[1] : LIST_TYPES[0]);
  };

  const handleImageNameUpdate = (event: ChangeEvent<HTMLInputElement>) => {
    setImageName(event.target.value);
  };

  function handleClose() {
    setErrorImageName(false);
    setModalNewNameOpen(false);
  }

  const handleImageNameChangeSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidImageName(imageName, imageExtension)) {
      setErrorImageName(true);
      return;
    }
    if (!currentImage.file) {
      return;
    }

    changeImageName({
      documentSuperId: currentImage.file.documentSuperId,
      imageSuperId: currentImage.file.superId,
      imageRevisionId: currentImage.file.revisionId,
      newName: fullFileName,
    });

    setErrorImageName(false);
    setModalNewNameOpen(false);
  };
  const ImageList = () => {
    const { t } = useTranslation('pictures');

    const images = (doc?.content?.images ?? []).map((image) => (
      <Picture
        listType={listType}
        imageData={(imageDataMap as Record<string, PictureData>)[image.revisionId]}
        key={image.revisionId}
        imageMenuItems={imageMenuItems}
        documentSuperId={documentId}
      />
    ));

    switch (listType) {
      case 'box':
        return (
          <div className="image--list">
            <div className="columns five gutter-12">{images}</div>
          </div>
        );
      case 'list':
      default:
        return (
          <table>
            <thead>
              <tr>
                <th></th>
                <th className="page-cell">{t('list-selector.list.thead.page')}</th>
                <th className="name-cell">{t('list-selector.list.thead.name')}</th>
                <th>{t('list-selector.list.thead.content-size')}</th>
                <th>{t('list-selector.list.thead.uploaded-at')}</th>
                <th>{t('list-selector.list.thead.actions')}</th>
              </tr>
            </thead>
            <tbody>{images}</tbody>
          </table>
        );
    }
  };

  return (
    <div>
      <style type="text/css">{css.data?.exposedCss}</style>
      <DetailView />
      <div className="overview--image">
        <div className="overview--image--menu">
          <button className="fab clear justify-self-end" onClick={toggleListType}>
            <i className="fa-regular fa-list" />
          </button>
          <div className="divider vertical" />
          <Tippy
            interactive={true}
            offset={[-130, 10]}
            placement="bottom"
            trigger="click"
            content={<UploadFile onSuccess={handleCreateSuccess} documentId={documentId} />}
          >
            <button className="fab">
              <i className="fa-regular fa-plus font-15" />
            </button>
          </Tippy>
        </div>
        <div className="overview--image--content">{!doc ? <Spinner /> : hasImages ? <ImageList /> : <PicturesEmptyState />}</div>
      </div>
      <Modal show={modalNewNameOpen}>
        <div className="header d-flex">
          <h3>{t('project-list:new-name.modal.title')}</h3>
          <div
            className="close"
            onClick={() => {
              handleClose();
            }}
          >
            <i className="fa-regular fa-xmark" />
          </div>
        </div>
        <div className="body ">
          <form onSubmit={handleImageNameChangeSubmit} className={errorImageName ? 'form with-footer error' : 'form with-footer'}>
            <div className="mt-4">
              <label>
                <input type="text" className="form-control" ref={(o) => o?.focus()} value={imageName} placeholder="" onChange={handleImageNameUpdate} />
              </label>
              <span className="text-help">{errorImageName ? t('project-list:new-name.field.nameError') : ''}</span>
            </div>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="button clear"
                onClick={() => {
                  handleClose();
                }}
              >
                {t('common:button.cancel')}
              </button>
              <button type="submit" className="button primary">
                {t('common:button.save')}
              </button>
            </div>
          </form>
        </div>
      </Modal>
      <ConfirmationModal
        message={
          <>
            <i className="fa-regular fa-trash-can fs-1 mb-4" />
            <h3 className="confirm-delete-header">
              {t('pictures:confirm-delete')}: {currentImage.filename}
            </h3>
            {isDeletingImage && <Spinner className="mt-3" />}
            <span className="text-gray-900 fs-7">{t('pictures:confirm-delete-info')}</span>
            {imageInUseError && <div className="text-red-500">{t('pictures:confirm-delete.image-in-use-error')}</div>}
          </>
        }
        title={' '}
        show={!!imageToDelete}
        onConfirm={handleConfirmDelete}
        onDecline={() => setImageToDelete(null)}
      />
    </div>
  );
};

export default Pictures;
